import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';
import { AgentEvents } from '@dreamai/types';
import { ReactComponent as PlusSmallIcon } from '../../../icons/plus-small.svg';
import './project-app-data-table-block.scss';

interface ColumnDef {
  key: string;
  label: string;
  type?: string;
  options?: string[];
}

interface ProjectAppDataTableBlockProps {
  blockId: string;
  data: Record<string, unknown>;
  embedded?: boolean;
}

const COLUMN_TYPES: { value: string; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'enum', label: 'Select' },
];

const ProjectAppDataTableBlock: React.FC<ProjectAppDataTableBlockProps> = ({
  blockId,
  data,
  embedded,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const dataTableId = data.dataTableId as string | undefined;
  const isSavingRef = useRef(false);

  const [columns, setColumns] = useState<ColumnDef[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  // Add column modal state
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColLabel, setNewColLabel] = useState('');
  const [newColType, setNewColType] = useState<string>('text');
  const [newColOptions, setNewColOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');

  // Add row modal state
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRowValues, setNewRowValues] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);

  // Fetch data table from the store
  const fetchData = useCallback(async () => {
    if (!dataTableId) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(`/data-tables/${dataTableId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success && res.data.dataTable) {
        const dt = res.data.dataTable;
        const rawCols: unknown[] = Array.isArray(dt.headings)
          ? dt.headings
          : [];
        // Defensive: drop any heading that isn't a well-formed object with a
        // non-empty string `key`. MUI X DataGrid crashes with
        // "Cannot read properties of undefined (reading 'field')" inside
        // getRowValue if any column definition's `field` is undefined, and
        // that's exactly what happens if we forward a heading with a blank
        // key straight through to GridColDef.field.
        const cols: ColumnDef[] = rawCols.filter(
          (c): c is ColumnDef =>
            !!c &&
            typeof c === 'object' &&
            typeof (c as ColumnDef).key === 'string' &&
            (c as ColumnDef).key.trim().length > 0,
        );
        const vals: Record<string, unknown>[] = Array.isArray(dt.values)
          ? dt.values
          : [];
        setColumns(cols);

        // Normalise row keys to match column keys (handles camelCase ↔ snake_case mismatches)
        const colKeys = cols.map((c) => c.key);
        const toSnake = (s: string) =>
          s.replace(/([A-Z])/g, '_$1').toLowerCase();
        const normalise = (row: Record<string, unknown>) => {
          const out: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(row)) {
            const snake = toSnake(k);
            if (colKeys.includes(k)) {
              out[k] = v;
            } else if (colKeys.includes(snake)) {
              out[snake] = v;
            } else {
              out[k] = v;
            }
          }
          return out;
        };

        setRows(
          vals.map((r: Record<string, unknown>, i: number) => ({
            ...normalise(r),
            _id: `row-${i}`,
          })),
        );
      }
    } catch (err) {
      console.error('[DataTable] Failed to fetch data table:', err);
    } finally {
      setLoading(false);
    }
  }, [dataTableId, getAccessTokenSilently]);

  useEffect(() => {
    if (!dataTableId) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [dataTableId, fetchData]);

  // Listen for Pusher events when another component updates this data table
  useEffect(() => {
    if (!pusherChannel || !dataTableId) return;
    const handler = (event: { dataTableId: string }) => {
      if (event.dataTableId === dataTableId && !isSavingRef.current) {
        fetchData();
      }
    };
    pusherChannel.bind(AgentEvents.DATA_TABLE_UPDATED, handler);
    return () => {
      pusherChannel.unbind(AgentEvents.DATA_TABLE_UPDATED, handler);
    };
  }, [pusherChannel, dataTableId, fetchData]);

  const persistData = useCallback(
    async (
      updatedColumns: ColumnDef[],
      updatedRows: Record<string, unknown>[],
    ) => {
      if (!dataTableId) return;
      setSaving(true);
      isSavingRef.current = true;
      try {
        const token = await getAccessTokenSilently();
        const cleanRows = updatedRows.map((r) => {
          const { _id, ...rest } = r;
          return rest;
        });
        await api.patch(
          `/data-tables/${dataTableId}`,
          { headings: updatedColumns, values: cleanRows },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (err) {
        console.error('[DataTable] Failed to save:', err);
      } finally {
        setSaving(false);
        setTimeout(() => {
          isSavingRef.current = false;
        }, 500);
      }
    },
    [dataTableId, getAccessTokenSilently],
  );

  // Convert our columns to MUI DataGrid columns. Filter again here — every
  // entry must have a non-empty string `key`, because an undefined `field`
  // makes MUI DataGrid's getRowValue throw synchronously the first time a
  // row is painted, taking down the whole canvas.
  const gridColumns: GridColDef[] = useMemo(() => {
    return columns
      .filter(
        (col): col is ColumnDef =>
          !!col && typeof col.key === 'string' && col.key.trim().length > 0,
      )
      .map((col) => {
        const def: GridColDef = {
          field: col.key,
          headerName: col.label,
          flex: 1,
          minWidth: 120,
          editable: true,
        };
        if (col.type === 'number') {
          def.type = 'number';
        } else if (col.type === 'boolean') {
          def.type = 'boolean';
        } else if (col.type === 'date') {
          def.type = 'date';
          def.valueGetter = (value: any) => {
            if (!value) return null;
            if (value instanceof Date) return value;
            return new Date(value as string);
          };
        } else if (col.type === 'enum' && col.options) {
          def.type = 'singleSelect';
          (def as any).valueOptions = col.options;
        }
        return def;
      });
  }, [columns]);

  // Convert rows for DataGrid (needs an `id` field)
  const gridRows: GridRowsProp = useMemo(() => {
    return rows.map((r) => ({
      id: r._id as string,
      ...r,
    }));
  }, [rows]);

  // Handle cell edit
  const handleProcessRowUpdate = useCallback(
    (newRow: any) => {
      const updatedRows = rows.map((r) =>
        r._id === newRow.id ? { ...r, ...newRow, _id: newRow.id } : r,
      );
      setRows(updatedRows);
      persistData(columns, updatedRows);
      return newRow;
    },
    [rows, columns, persistData],
  );

  // Add column
  const handleAddColumn = useCallback(() => {
    if (!newColLabel.trim()) return;
    const key = newColLabel
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    if (columns.some((c) => c.key === key)) return;

    const colDef: ColumnDef = {
      key,
      label: newColLabel.trim(),
      type: newColType,
    };
    if (newColType === 'enum' && newColOptions.length > 0) {
      colDef.options = newColOptions;
    }
    const updatedColumns = [...columns, colDef];
    setColumns(updatedColumns);
    setNewColLabel('');
    setNewColType('text');
    setNewColOptions([]);
    setNewOptionInput('');
    setShowAddColumn(false);
    persistData(updatedColumns, rows);
  }, [newColLabel, newColType, newColOptions, columns, rows, persistData]);

  // Add row
  const handleAddRow = useCallback(() => {
    const newRow: Record<string, unknown> = { _id: `row-${Date.now()}` };
    for (const col of columns) {
      const val = newRowValues[col.key] || '';
      if (col.type === 'number') {
        newRow[col.key] = val ? Number(val) : null;
      } else if (col.type === 'boolean') {
        newRow[col.key] = val === 'true';
      } else {
        newRow[col.key] = val;
      }
    }
    const updatedRows = [...rows, newRow];
    setRows(updatedRows);
    setNewRowValues({});
    setShowAddRow(false);
    persistData(columns, updatedRows);
  }, [columns, rows, newRowValues, persistData]);

  // Delete row
  const handleDeleteRow = useCallback(
    (rowId: string) => {
      const updatedRows = rows.filter((r) => r._id !== rowId);
      setRows(updatedRows);
      persistData(columns, updatedRows);
    },
    [rows, columns, persistData],
  );

  if (loading) {
    return <div className='project-app-data-table-block'>Loading…</div>;
  }

  if (!dataTableId) {
    return (
      <div className='project-app-data-table-block'>No data table linked.</div>
    );
  }

  return (
    <div className='project-app-data-table-block'>
      <div className='grid-container'>
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          processRowUpdate={handleProcessRowUpdate}
          disableRowSelectionOnClick
          hideFooterPagination
          hideFooter
          autoHeight
          density='compact'
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#f7f3ed',
              borderColor: '#ebe9e4',
              borderRight: '1px solid #ebe9e4',
              '&:last-of-type': {
                borderRight: 'none',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontFamily: 'Geist, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#000d26',
              textTransform: 'none',
              letterSpacing: 'normal',
            },
            '& .MuiDataGrid-cell': {
              fontFamily: 'Geist, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#000d26',
              borderColor: '#ebe9e4',
              borderRight: '1px solid #ebe9e4',
              padding: '8px 12px',
              backgroundColor: '#fffefc',
              '&:last-of-type': {
                borderRight: 'none',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(55, 53, 47, 0.04)',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-row:first-of-type .MuiDataGrid-cell': {
              borderTop: 'none',
            },
            '& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
              borderBottom: '1px solid #ebe9e4',
            },
            '& .MuiDataGrid-filler': {
              backgroundColor: '#f7f3ed',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
            },
          }}
        />
      </div>

      <div className='toolbar'>
        <PlusSmallIcon className='toolbar-plus-icon' />
        {!embedded && (
          <button
            className='toolbar-btn'
            onClick={() => setShowAddColumn(true)}
          >
            Column
          </button>
        )}
        <button
          className='toolbar-btn'
          onClick={() => {
            setNewRowValues({});
            setShowAddRow(true);
          }}
        >
          Row
        </button>
        {saving && <span className='save-indicator'>Saving…</span>}
      </div>

      {/* Add Column Modal */}
      {showAddColumn && (
        <div
          className='dt-modal-overlay'
          onClick={() => setShowAddColumn(false)}
        >
          <div className='dt-modal' onClick={(e) => e.stopPropagation()}>
            <div className='dt-modal-header'>
              <h2>Add Column</h2>
              <button
                className='dt-modal-cancel'
                onClick={() => setShowAddColumn(false)}
              >
                Cancel
              </button>
            </div>
            <div className='dt-modal-body'>
              <div className='dt-field'>
                <label>Name</label>
                <input
                  type='text'
                  value={newColLabel}
                  onChange={(e) => setNewColLabel(e.target.value)}
                  placeholder='e.g. Revenue'
                  autoFocus
                />
              </div>
              <div className='dt-field'>
                <label>Type</label>
                <div className='dt-type-options'>
                  {COLUMN_TYPES.map((t) => (
                    <button
                      key={t.value}
                      className={`dt-type-option${newColType === t.value ? ' active' : ''}`}
                      onClick={() => setNewColType(t.value)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              {newColType === 'enum' && (
                <div className='dt-field'>
                  <label>Options</label>
                  <div className='dt-enum-options'>
                    {newColOptions.map((opt, i) => (
                      <span key={i} className='dt-enum-tag'>
                        {opt}
                        <button
                          type='button'
                          className='dt-enum-tag-remove'
                          onClick={() =>
                            setNewColOptions((prev) =>
                              prev.filter((_, idx) => idx !== i),
                            )
                          }
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                    <input
                      type='text'
                      className='dt-enum-input'
                      value={newOptionInput}
                      onChange={(e) => setNewOptionInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ',') &&
                          newOptionInput.trim()
                        ) {
                          e.preventDefault();
                          const val = newOptionInput.trim();
                          if (!newColOptions.includes(val)) {
                            setNewColOptions((prev) => [...prev, val]);
                          }
                          setNewOptionInput('');
                        } else if (
                          e.key === 'Backspace' &&
                          !newOptionInput &&
                          newColOptions.length > 0
                        ) {
                          setNewColOptions((prev) => prev.slice(0, -1));
                        }
                      }}
                      placeholder={
                        newColOptions.length === 0
                          ? 'Type an option and press Enter'
                          : 'Add another…'
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div className='dt-modal-footer'>
              <button
                className='dt-btn-primary'
                onClick={handleAddColumn}
                disabled={!newColLabel.trim()}
              >
                Add Column
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Row Modal */}
      {showAddRow && (
        <div className='dt-modal-overlay' onClick={() => setShowAddRow(false)}>
          <div className='dt-modal' onClick={(e) => e.stopPropagation()}>
            <div className='dt-modal-header'>
              <h2>Add Entry</h2>
              <button
                className='dt-modal-cancel'
                onClick={() => setShowAddRow(false)}
              >
                Cancel
              </button>
            </div>
            <div className='dt-modal-body'>
              {columns.map((col) => (
                <div className='dt-field' key={col.key}>
                  <label>{col.label}</label>
                  {col.type === 'boolean' ? (
                    <div
                      className={`dt-toggle${newRowValues[col.key] === 'true' ? ' active' : ''}`}
                      onClick={() =>
                        setNewRowValues((prev) => ({
                          ...prev,
                          [col.key]:
                            prev[col.key] === 'true' ? 'false' : 'true',
                        }))
                      }
                    >
                      <div className='dt-toggle-thumb' />
                    </div>
                  ) : col.type === 'enum' && col.options ? (
                    <select
                      value={newRowValues[col.key] || ''}
                      onChange={(e) =>
                        setNewRowValues((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }))
                      }
                    >
                      <option value=''>Select…</option>
                      {col.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={
                        col.type === 'number'
                          ? 'number'
                          : col.type === 'date'
                            ? 'date'
                            : col.type === 'email'
                              ? 'email'
                              : col.type === 'url'
                                ? 'url'
                                : 'text'
                      }
                      value={newRowValues[col.key] || ''}
                      onChange={(e) =>
                        setNewRowValues((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }))
                      }
                      placeholder={col.label}
                    />
                  )}
                </div>
              ))}
              {columns.length === 0 && (
                <p className='dt-empty-hint'>Add some columns first.</p>
              )}
            </div>
            <div className='dt-modal-footer'>
              <button
                className='dt-btn-primary'
                onClick={handleAddRow}
                disabled={columns.length === 0}
              >
                Add Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAppDataTableBlock;
